import { type ChangeEvent, memo, useCallback, useId } from 'react';

import { cn } from '@air/utils-tailwind';

import { useBoundedNumberField } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useBoundedNumberField';
import { clampBoundedNumberString } from '~/components/v2/components/workspace/workspace-agent-panel/utils/clampBoundedNumberString';

export const WORKSPACE_AGENT_GUIDED_VARIANT_NUMBER_UNITS = [
  { id: 'percent', label: '%' },
  { id: 'px', label: 'px' },
] as const;

export type WorkspaceAgentGuidedVariantNumberUnitId =
  (typeof WORKSPACE_AGENT_GUIDED_VARIANT_NUMBER_UNITS)[number]['id'];

export type WorkspaceAgentGuidedVariantNumberUnitFieldProps = {
  'aria-describedby': string | undefined;
  disabled?: boolean;
  id: string;
  /** `null` means that unit has no upper bound yet. */
  max: Record<WorkspaceAgentGuidedVariantNumberUnitId, number | null>;
  min: number;
  onUnitChange: (unit: WorkspaceAgentGuidedVariantNumberUnitId) => void;
  onValueChange: (value: number) => void;
  unit: WorkspaceAgentGuidedVariantNumberUnitId;
  value: number;
};

export const WorkspaceAgentGuidedVariantNumberUnitField = memo(
  ({
    'aria-describedby': describedBy,
    disabled = false,
    id,
    max,
    min,
    onUnitChange,
    onValueChange,
    unit,
    value,
  }: WorkspaceAgentGuidedVariantNumberUnitFieldProps) => {
    const unitGroupName = useId();
    const {
      inputMax,
      onBlur,
      onChange,
      value: inputValue,
    } = useBoundedNumberField({
      max: max[unit],
      min,
      onChange: onValueChange,
      value,
    });

    const handleUnitChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const nextUnit = WORKSPACE_AGENT_GUIDED_VARIANT_NUMBER_UNITS.find(
          (option) => option.id === event.currentTarget.value,
        );

        if (!nextUnit) {
          return;
        }

        onUnitChange(nextUnit.id);
        onValueChange(
          clampBoundedNumberString({
            max: max[nextUnit.id],
            min,
            raw: inputValue,
          }),
        );
      },
      [inputValue, max, min, onUnitChange, onValueChange],
    );

    return (
      <div className="flex h-8 w-28 items-center rounded-8 border border-gray-5 bg-gray-1 px-2.5 focus-within:border-blue-9 focus-within:ring-1 focus-within:ring-blue-9">
        <input
          aria-describedby={describedBy}
          className="min-w-0 flex-1 bg-transparent text-12 text-gray-12 outline-none [appearance:textfield] disabled:text-gray-9 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_NUMBER"
          disabled={disabled}
          id={id}
          inputMode="numeric"
          max={inputMax}
          min={min}
          onBlur={onBlur}
          onChange={onChange}
          type="number"
          value={inputValue}
        />
        <fieldset className="m-0 flex shrink-0 items-center gap-1.5 border-0 p-0 text-12">
          <legend className="sr-only">Unit</legend>
          {WORKSPACE_AGENT_GUIDED_VARIANT_NUMBER_UNITS.map((option) => {
            const isSelected = option.id === unit;

            return (
              <label
                className={cn(
                  'relative rounded-4 px-1 has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-blue-8',
                  disabled ? 'cursor-default' : 'cursor-pointer',
                  isSelected ? 'bg-gray-3 text-gray-12' : 'text-gray-10 hover:text-gray-11',
                )}
                key={option.id}
              >
                <input
                  checked={isSelected}
                  className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-default"
                  data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_NUMBER_UNIT"
                  data-unit-id={option.id}
                  disabled={disabled}
                  name={unitGroupName}
                  onChange={handleUnitChange}
                  type="radio"
                  value={option.id}
                />
                {option.label}
              </label>
            );
          })}
        </fieldset>
      </div>
    );
  },
);

WorkspaceAgentGuidedVariantNumberUnitField.displayName = 'WorkspaceAgentGuidedVariantNumberUnitField';
